import {
  As,
  BorderProps,
  HStack,
  Icon,
  Stack,
  StackDirection,
  Text,
  TextProps,
} from '@chakra-ui/react';
import { OfficeBuildingIcon, UserIcon } from '@heroicons/react/outline';
import {
  OfficeBuildingIcon as OfficeBuildingIconSelected,
  UserIcon as UserIconSelected,
} from '@heroicons/react/solid';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import { IdentityType } from '@blockpulse3/graphql/hooks';

export type SwitchIdentityTypeChange = { prev: IdentityType; next: IdentityType };

type Props = {
  /* ** Current identity type value ** */
  value?: IdentityType;
  /* ** Direction ** */
  direction?: StackDirection;
  /* ** Is switch group disabled ** */
  disabled?: boolean;
  /* ** On identity type change, return by args previous and next state ** */
  onChange?: (values: SwitchIdentityTypeChange) => void;
};

/**
 * SwitchIdentityType.
 * TODO: use useRadioGroup() but difficult to set value outside component.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SwitchIdentityType({
  value = IdentityType.COMPANY,
  disabled = false,
  direction = 'row',
  onChange = noop,
}: Props): JSX.Element {
  const i18nSwitchIdentityType = useTranslations('SubscriberInformationsForm');

  /* ** Border status props ** */
  const getBorderProps = (idType: IdentityType): BorderProps => {
    return {
      borderWidth: idType === value ? '2px' : '1px',
      borderColor: idType === value ? 'primary' : 'gray.300',
      borderStyle: 'solid',
    };
  };

  /* ** Icon status ** */
  const getIcon = (idType: IdentityType): As => {
    if (idType === IdentityType.COMPANY) {
      return idType === value ? OfficeBuildingIconSelected : OfficeBuildingIcon;
    }
    return idType === value ? UserIconSelected : UserIcon;
  };

  /* ** Font weight status ** */
  const getFontWeight = (idType: IdentityType): TextProps['fontWeight'] => {
    return idType === value ? '600' : '500';
  };

  const handleClick = (idType: IdentityType): void => {
    onChange({ prev: value, next: idType });
  };

  return (
    <Stack direction={direction} spacing="5">
      <HStack
        cursor="pointer"
        opacity={disabled ? '0.5' : '1'}
        p="2"
        rounded="md"
        spacing="2"
        w="full"
        onClick={(): void => handleClick(IdentityType.COMPANY)}
        {...getBorderProps(IdentityType.COMPANY)}
      >
        <Icon as={getIcon(IdentityType.COMPANY)} boxSize="18px" color="gray.900" />
        <Text fontWeight={getFontWeight(IdentityType.COMPANY)}>
          {i18nSwitchIdentityType('RadioLegal')}
        </Text>
      </HStack>
      <HStack
        cursor="pointer"
        opacity={disabled ? '0.5' : '1'}
        p="2"
        rounded="md"
        spacing="2"
        w="full"
        onClick={(): void => handleClick(IdentityType.INDIVIDUAL)}
        {...getBorderProps(IdentityType.INDIVIDUAL)}
      >
        <Icon as={getIcon(IdentityType.INDIVIDUAL)} boxSize="18px" color="gray.900" />
        <Text fontWeight={getFontWeight(IdentityType.INDIVIDUAL)}>
          {i18nSwitchIdentityType('RadioNatural')}
        </Text>
      </HStack>
    </Stack>
  );
}

export type SwitchIdentityTypeProps = Props;
